import React, { useState, useEffect } from 'react';
import {
  performanceMonitor,
  PerformanceMetric,
  checkPerformanceBudget,
} from '@/utils/performance';
import {
  ChartBarIcon,
  ClockIcon,
  CpuChipIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

interface PerformanceDashboardProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PerformanceDashboard: React.FC<PerformanceDashboardProps> = ({
  isOpen,
  onClose,
}) => {
  const [metrics, setMetrics] = useState<PerformanceMetric[]>([]);
  const [violations, setViolations] = useState<string[]>([]);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(
    null
  );

  useEffect(() => {
    if (isOpen) {
      const updateMetrics = () => {
        setMetrics(performanceMonitor.getMetrics());
        setViolations(checkPerformanceBudget());
      };

      updateMetrics();
      const interval = setInterval(updateMetrics, 5000);
      setRefreshInterval(interval);

      return () => {
        if (interval) clearInterval(interval);
      };
    } else {
      if (refreshInterval) {
        clearInterval(refreshInterval);
        setRefreshInterval(null);
      }
    }
  }, [isOpen]);

  const getMetricSummary = (name: string) => {
    const relevantMetrics = metrics.filter((m) => m.name === name);
    if (relevantMetrics.length === 0) return null;

    const values = relevantMetrics.map((m) => m.value);
    return {
      count: values.length,
      average: values.reduce((a, b) => a + b, 0) / values.length,
      min: Math.min(...values),
      max: Math.max(...values),
      latest: values[values.length - 1],
    };
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms.toFixed(1)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatBytes = (bytes: number) => {
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  const exportMetrics = () => {
    const data = performanceMonitor.exportMetrics();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `performance-metrics-${new Date().toISOString()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  const pageLoadSummary = getMetricSummary('page-load-time');
  const domLoadSummary = getMetricSummary('dom-content-loaded');
  const memorySummary = getMetricSummary('memory-used');
  const longTaskSummary = getMetricSummary('long-task');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
            <ChartBarIcon className="h-6 w-6 mr-2" />
            Performance Dashboard
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <span className="sr-only">Close</span>
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Performance Budget Violations */}
          {violations.length > 0 && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-center mb-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Performance Budget Violations
                </h3>
              </div>
              <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
                {violations.map((violation, index) => (
                  <li key={index}>• {violation}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Key Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Page Load Time */}
            {pageLoadSummary && (
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="flex items-center mb-2">
                  <ClockIcon className="h-5 w-5 text-blue-500 mr-2" />
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    Page Load
                  </h4>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatDuration(pageLoadSummary.average)}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Range: {formatDuration(pageLoadSummary.min)} -{' '}
                  {formatDuration(pageLoadSummary.max)}
                </div>
              </div>
            )}

            {/* DOM Content Loaded */}
            {domLoadSummary && (
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="flex items-center mb-2">
                  <ClockIcon className="h-5 w-5 text-green-500 mr-2" />
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    DOM Ready
                  </h4>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatDuration(domLoadSummary.average)}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Range: {formatDuration(domLoadSummary.min)} -{' '}
                  {formatDuration(domLoadSummary.max)}
                </div>
              </div>
            )}

            {/* Memory Usage */}
            {memorySummary && (
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="flex items-center mb-2">
                  <CpuChipIcon className="h-5 w-5 text-purple-500 mr-2" />
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    Memory
                  </h4>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatBytes(memorySummary.latest)}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Peak: {formatBytes(memorySummary.max)}
                </div>
              </div>
            )}

            {/* Long Tasks */}
            {longTaskSummary && (
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="flex items-center mb-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-orange-500 mr-2" />
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    Long Tasks
                  </h4>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {longTaskSummary.count}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Avg: {formatDuration(longTaskSummary.average)}
                </div>
              </div>
            )}
          </div>

          {/* Recent Metrics */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Recent Metrics
            </h3>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden">
              <div className="max-h-64 overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                  <thead className="bg-gray-100 dark:bg-gray-600 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Metric
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Value
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Time
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                    {metrics
                      .slice(-20)
                      .reverse()
                      .map((metric, index) => (
                        <tr
                          key={index}
                          className="hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                            {metric.name}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                            {metric.name.includes('memory')
                              ? formatBytes(metric.value)
                              : formatDuration(metric.value)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                            {new Date(metric.timestamp).toLocaleTimeString()}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Total metrics: {metrics.length} | Auto-refresh: 5s
            </div>
            <div className="space-x-2">
              <button
                onClick={() => performanceMonitor.clearMetrics()}
                className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500"
              >
                Clear Metrics
              </button>
              <button
                onClick={exportMetrics}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Export Data
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
